import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2Icon, SunIcon, SettingsIcon, PlayIcon, ShieldIcon, MicIcon } from 'lucide-react';
type RoomZone = 'indoor' | 'outdoor';
type Room = {
  id: string;
  name: string;
  zone: RoomZone;
  top: string;
  left: string;
  width: string;
  height: string;
  color: string;
  colorClass: string;
  bgClass: string;
  audio: string;
  scenes: string[];
  securityModes?: {
    name: string;
    desc: string;
  }[];
  performanceModes?: string[];
};
const allRooms: Room[] = [
// ── Indoor Rooms ──
{
  id: 'living',
  name: 'Living Room',
  zone: 'indoor',
  top: '3%',
  left: '3%',
  width: '45%',
  height: '28%',
  color: '#F59E0B',
  colorClass: 'text-[#F59E0B]',
  bgClass: 'bg-[#F59E0B]',
  audio: '4.1 Surround Zone',
  scenes: ['Morning', 'Entertain', 'Relax']
}, {
  id: 'theatre',
  name: 'Private Theatre',
  zone: 'indoor',
  top: '3%',
  left: '52%',
  width: '45%',
  height: '28%',
  color: '#6366F1',
  colorClass: 'text-[#6366F1]',
  bgClass: 'bg-[#6366F1]',
  audio: '11.2.4 Dolby Atmos',
  scenes: ['Movie Start', 'Intermission', 'Credits']
}, {
  id: 'lounge',
  name: 'Bar & Lounge',
  zone: 'indoor',
  top: '35%',
  left: '3%',
  width: '29%',
  height: '20%',
  color: '#F97316',
  colorClass: 'text-[#F97316]',
  bgClass: 'bg-[#F97316]',
  audio: 'Multi-Zone Party System',
  scenes: ['Cocktail Hour', 'DJ Mode', 'Late Night']
}, {
  id: 'karaoke',
  name: 'Performance Room',
  zone: 'indoor',
  top: '35%',
  left: '36%',
  width: '28%',
  height: '20%',
  color: '#F97316',
  colorClass: 'text-[#F97316]',
  bgClass: 'bg-[#F97316]',
  audio: 'Stage Monitor + Karaoke',
  scenes: ['Karaoke Night', 'Live Performance', 'Private Club'],
  performanceModes: ['Vocal Focus', 'Concert Hall', 'Studio Dry']
}, {
  id: 'bedroom',
  name: 'Master Suite',
  zone: 'indoor',
  top: '35%',
  left: '68%',
  width: '29%',
  height: '20%',
  color: '#10B981',
  colorClass: 'text-[#10B981]',
  bgClass: 'bg-[#10B981]',
  audio: 'Stereo + Sub Zone',
  scenes: ['Wake Up', 'Read', 'Goodnight']
}, {
  id: 'bathroom',
  name: 'Wellness Bath',
  zone: 'indoor',
  top: '59%',
  left: '3%',
  width: '94%',
  height: '12%',
  color: '#EC4899',
  colorClass: 'text-[#EC4899]',
  bgClass: 'bg-[#EC4899]',
  audio: 'Invisible Stereo',
  scenes: ['Spa', 'Energize', 'Nightlight']
},
// ── Outdoor Rooms ──
{
  id: 'terrace',
  name: 'Terrace',
  zone: 'outdoor',
  top: '76%',
  left: '3%',
  width: '28%',
  height: '21%',
  color: '#F59E0B',
  colorClass: 'text-[#F59E0B]',
  bgClass: 'bg-[#F59E0B]',
  audio: 'Landscape Array',
  scenes: ['Sunset', 'Party', 'Starlight']
}, {
  id: 'garden',
  name: 'Garden & Pool',
  zone: 'outdoor',
  top: '76%',
  left: '35%',
  width: '37%',
  height: '21%',
  color: '#22D3EE',
  colorClass: 'text-[#22D3EE]',
  bgClass: 'bg-[#22D3EE]',
  audio: 'Landscape Speaker Array',
  scenes: ['Pool Party', 'Sunset Lounge', 'Night Garden']
}, {
  id: 'entry',
  name: 'Entry & Gate',
  zone: 'outdoor',
  top: '76%',
  left: '76%',
  width: '21%',
  height: '21%',
  color: '#64748B',
  colorClass: 'text-[#64748B]',
  bgClass: 'bg-[#64748B]',
  audio: 'Welcome Announcement',
  scenes: ['Arrival Mode', 'Away Mode', 'Alert Mode'],
  securityModes: [{
    name: 'Away Mode',
    desc: 'Lights simulate presence, alerts active'
  }, {
    name: 'Arrival Mode',
    desc: 'Gate opens, lights on, music starts'
  }, {
    name: 'Alert Mode',
    desc: 'Instant notifications, camera snapshots'
  }]
}];
export function HomeExplorer() {
  const [selectedRoom, setSelectedRoom] = useState<Room>(allRooms[0]);
  return <section id="spaces" className="py-32 bg-void relative border-t border-graphite" data-id="element-339">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-340">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-12" data-id="element-341">
          <h2 className="text-headline font-serif text-platinum mb-4" data-id="element-342">
            Explore Every Space
          </h2>
          <p className="text-silver max-w-2xl" data-id="element-343">
            Tap any room — indoors or out — to reveal the invisible technology
            within. Your home is one connected experience.
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-12" data-id="element-344">
          {/* Unified Floor Plan */}
          <div className="w-full lg:w-3/5 aspect-square md:aspect-[4/3] bg-carbon border border-graphite rounded-2xl p-4 md:p-6 relative overflow-hidden" data-id="element-345">
            {/* Blueprint dot grid */}
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#8A8A8A 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }} data-id="element-346" />

            {/* Indoor zone label */}
            <div className="absolute top-3 left-4 z-20 flex items-center gap-2" data-id="element-347">
              <div className="w-1.5 h-1.5 rounded-full bg-platinum opacity-40" data-id="element-348" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-silver/50 font-medium" data-id="element-349">
                Indoor
              </span>
            </div>

            {/* Outdoor zone label */}
            <div className="absolute bottom-3 left-4 z-20 flex items-center gap-2" data-id="element-350">
              <div className="w-1.5 h-1.5 rounded-full bg-[#22D3EE] opacity-40" data-id="element-351" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#22D3EE]/40 font-medium" data-id="element-352">
                Outdoor
              </span>
            </div>

            {/* Subtle indoor/outdoor boundary */}
            <div className="absolute left-[3%] right-[3%] z-10 border-t border-dashed border-silver/10" style={{
            top: '74%'
          }} data-id="element-353" />

            {/* All rooms in one view */}
            <div className="absolute inset-4 md:inset-6" data-id="element-354">
              {allRooms.map(room => {
              const isSelected = selectedRoom.id === room.id;
              const isOutdoor = room.zone === 'outdoor';
              return <motion.div key={room.id} onClick={() => setSelectedRoom(room)} className={`absolute border transition-all duration-300 cursor-pointer flex items-center justify-center group ${isSelected ? 'z-10' : 'border-graphite hover:border-silver/50'} ${isOutdoor && !isSelected ? 'bg-void/60 border-dashed' : !isSelected ? 'bg-obsidian/50' : ''}`} style={{
                top: room.top,
                left: room.left,
                width: room.width,
                height: room.height,
                borderColor: isSelected ? room.color : undefined,
                backgroundColor: isSelected ? `${room.color}1A` : undefined,
                boxShadow: isSelected ? `0 0 25px ${room.color}30` : 'none'
              }} whileHover={{
                scale: 0.98
              }} data-id="element-355">
                    {isSelected && <motion.div layoutId="room-highlight" className="absolute inset-0 border-2" style={{
                  borderColor: room.color
                }} initial={false} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                }} data-id="element-356" />}

                    <div className="flex flex-col items-center gap-0.5 px-1" data-id="element-357">
                      <span className={`font-serif tracking-widest uppercase text-[10px] md:text-xs transition-colors text-center leading-tight ${isSelected ? '' : 'text-silver group-hover:text-platinum'}`} style={{
                    color: isSelected ? room.color : undefined
                  }} data-id="element-358">
                        {room.name}
                      </span>
                      {isOutdoor && !isSelected && <span className="text-[8px] tracking-wider uppercase text-silver/30 hidden md:block" data-id="element-359">
                          outdoor
                        </span>}
                    </div>

                    {isSelected && <span className="absolute top-2 right-2 w-2 h-2 rounded-full animate-pulse" style={{
                  backgroundColor: room.color
                }} data-id="element-360" />}
                  </motion.div>;
            })}
            </div>
          </div>

          {/* Room Details Panel */}
          <div className="w-full lg:w-2/5 flex flex-col justify-center" data-id="element-361">
            <AnimatePresence mode="wait" data-id="element-362">
              <motion.div key={selectedRoom.id} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.4
            }} className="bg-obsidian border border-graphite rounded-2xl p-8 relative overflow-hidden flex flex-col" data-id="element-363">
                {/* Background glow */}
                <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[80px] opacity-20" style={{
                backgroundColor: selectedRoom.color
              }} data-id="element-364" />

                {/* Zone badge */}
                <div className="flex items-center gap-3 mb-4 relative z-10" data-id="element-365">
                  <span className="text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full border" style={{
                  borderColor: `${selectedRoom.color}40`,
                  color: selectedRoom.color
                }} data-id="element-366">
                    {selectedRoom.zone}
                  </span>
                </div>

                <h3 className="text-title font-serif mb-8 relative z-10" style={{
                color: selectedRoom.color
              }} data-id="element-367">
                  {selectedRoom.name}
                </h3>

                <div className="space-y-8 relative z-10 flex-1 overflow-y-auto pr-2" data-id="element-368">
                  {/* Audio */}
                  <div data-id="element-369">
                    <div className="flex items-center text-platinum mb-3" data-id="element-370">
                      <Volume2Icon className="w-5 h-5 mr-3" style={{
                      color: selectedRoom.color
                    }} data-id="element-371" />
                      <h4 className="font-serif tracking-wide" data-id="element-372">
                        Audio Architecture
                      </h4>
                    </div>
                    <p className="text-silver text-sm pl-8" data-id="element-373">
                      {selectedRoom.audio}
                    </p>
                  </div>

                  {/* Scenes */}
                  <div data-id="element-374">
                    <div className="flex items-center text-platinum mb-3" data-id="element-375">
                      <SunIcon className="w-5 h-5 mr-3" style={{
                      color: selectedRoom.color
                    }} data-id="element-376" />
                      <h4 className="font-serif tracking-wide" data-id="element-377">
                        Lighting Scenes
                      </h4>
                    </div>
                    <div className="flex flex-wrap gap-2 pl-8" data-id="element-378">
                      {selectedRoom.scenes.map(scene => <span key={scene} className="px-3 py-1 text-xs border border-graphite rounded-full text-platinum bg-carbon" data-id="element-379">
                          {scene}
                        </span>)}
                    </div>
                  </div>

                  {/* Security Modes */}
                  {selectedRoom.securityModes && <div data-id="element-380">
                      <div className="flex items-center text-platinum mb-3" data-id="element-381">
                        <ShieldIcon className="w-5 h-5 mr-3" style={{
                      color: selectedRoom.color
                    }} data-id="element-382" />
                        <h4 className="font-serif tracking-wide" data-id="element-383">
                          Security Modes
                        </h4>
                      </div>
                      <div className="space-y-3 pl-8" data-id="element-384">
                        {selectedRoom.securityModes.map((mode, i) => <div key={i} data-id="element-385">
                            <span className="text-platinum text-sm block mb-0.5" data-id="element-386">
                              {mode.name}
                            </span>
                            <span className="text-silver text-xs block" data-id="element-387">
                              {mode.desc}
                            </span>
                          </div>)}
                      </div>
                    </div>}

                  {/* Performance Modes */}
                  {selectedRoom.performanceModes && <div data-id="element-388">
                      <div className="flex items-center text-platinum mb-3" data-id="element-389">
                        <MicIcon className="w-5 h-5 mr-3" style={{
                      color: selectedRoom.color
                    }} data-id="element-390" />
                        <h4 className="font-serif tracking-wide" data-id="element-391">
                          Performance Modes
                        </h4>
                      </div>
                      <div className="flex flex-wrap gap-2 pl-8" data-id="element-392">
                        {selectedRoom.performanceModes.map(mode => <span key={mode} className="px-3 py-1 text-xs border border-graphite rounded-full text-platinum bg-carbon" data-id="element-393">
                            {mode}
                          </span>)}
                      </div>
                    </div>}

                  {/* Control Preview */}
                  <div className="pt-6 border-t border-graphite" data-id="element-394">
                    <div className="flex items-center text-platinum mb-4" data-id="element-395">
                      <SettingsIcon className="w-5 h-5 mr-3" style={{
                      color: selectedRoom.color
                    }} data-id="element-396" />
                      <h4 className="font-serif tracking-wide" data-id="element-397">
                        Control Preview
                      </h4>
                    </div>
                    <div className="ml-8 bg-carbon border border-graphite rounded-lg p-4 flex items-center justify-between" data-id="element-398">
                      <div className="flex items-center space-x-3" data-id="element-399">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
                        backgroundColor: `${selectedRoom.color}33`
                      }} data-id="element-400">
                          <PlayIcon className="w-4 h-4 ml-0.5" style={{
                          color: selectedRoom.color
                        }} data-id="element-401" />
                        </div>
                        <div data-id="element-402">
                          <p className="text-xs text-silver" data-id="element-403">Now Playing</p>
                          <p className="text-sm text-platinum" data-id="element-404">Ambient Mix</p>
                        </div>
                      </div>
                      <div className="w-16 h-1 bg-graphite rounded-full overflow-hidden" data-id="element-405">
                        <div className="w-2/3 h-full" style={{
                        backgroundColor: selectedRoom.color
                      }} data-id="element-406" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}